import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { useParams, useHistory } from 'react-router-dom';
import uniqueId from 'lodash/uniqueId';
import { PrimaryButton, DefaultButton, IconButton } from 'office-ui-fabric-react/lib/Button';
import { fetchContract, submitForApproval } from '../../actions/contracts';
import { fetchAgreements } from '../../actions/agreements';
import TextButton from '../../components/TextButton';
import AgreementForm from '../../components/AgreementForm';
import { useAgreementForms } from '../../context/agreement-forms';
import Module from '../../components/Module';
import ModuleHeader from '../../components/ModuleHeader';
import ModuleContent from '../../components/ModuleContent';
import ModuleContentActions from '../../components/ModuleContentActions';
import SubModule from '../../components/SubModule';
import SubModuleHeader from '../../components/SubModuleHeader';
import SubModuleContent from '../../components/SubModuleContent';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import SavingIndicator from '../../components/SavingIndicator';
import styles from './AddContractAgreements.v1.style';

/**
 * This page is only for initial contract creation!
 */
const AddContractAgreementsV1 = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { submitForms, areSubmitting, unRegisterForm } = useAgreementForms();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { contractAddress } = useParams();
  const agreementAddresses = useSelector(state =>
    get(state, `entities.contracts.${contractAddress}.agreements`, []),
  );
  const [newAgreementIds, setNewAgreementIds] = useState([]);
  // combine existing agreement addresses, and newly created agreements
  const agreementIds = [...agreementAddresses, ...newAgreementIds];
  const contract = useSelector(state => get(state, `entities.contracts.${contractAddress}`, {}));
  const digitizedContract = useSelector(state =>
    get(state, `entities.digitizedcontracts.${contract.contractId}`, null),
  );

  // if the contract isn't draft, then you shouldn't be able to edit it
  // here in this user flow
  useEffect(() => {
    if (contract.approvalStatus && contract.approvalStatus !== 'Draft' && !isSubmitting) {
      history.push(`/contracts/${contractAddress}`);
    }
  }, [history, contract.approvalStatus, contractAddress, isSubmitting]);

  useEffect(() => {
    dispatch(fetchContract(contractAddress));

    async function fetchExistingAgreements() {
      const agreements = await dispatch(fetchAgreements(contractAddress));
      if (!agreements?.payload.result.length) {
        // create a new agreement form if there's no existing agreements
        setNewAgreementIds([uniqueId('agreement_form_')]);
      }
    }
    fetchExistingAgreements();
  }, [dispatch, contractAddress]);

  const handleAddAgreement = () =>
    setNewAgreementIds([...newAgreementIds, uniqueId('agreement_form_')]);

  const handleRemoveAgreement = id => {
    unRegisterForm(id);
    setNewAgreementIds(newAgreementIds.filter(newAgreementId => newAgreementId !== id));
  };

  const handleCancel = async () => {
    history.push(`/contracts/${contractAddress}`);
  };

  const handleSave = async () => {
    // Checking if dirty on agreement page won't work cause digitized contract fields
    // are populated and initial values never changed. This tries to ensure that
    // in the situation a user is uploading a new digitized contract, and they
    // accept all the initial pre-populated values, it's still submitted. Scenario
    // only happens when there's NO agreements existing and a matching digitized contract.
    const forceSubmission = agreementAddresses.length === 0 && digitizedContract;
    const results = await submitForms(forceSubmission);

    if (results.success) {
      history.push(`/contracts/${contractAddress}`);
    }
  };

  const handleSubmitForApproval = async () => {
    const forceSubmission = agreementAddresses.length === 0 && digitizedContract;
    // Setting ingoreDirty parameter to true for digitizedContract flow
    const results = await submitForms(forceSubmission);

    if (results.success) {
      setIsSubmitting(true);
      const response = await dispatch(submitForApproval(contractAddress));

      if (!response.error) {
        history.push(`/contracts/${contractAddress}`);
      } else {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <>
      <PageTitle>Add Contract</PageTitle>

      <Main>
        <Module className={styles.agreementModule}>
          <ModuleHeader variant="xLarge">Royalty Fees</ModuleHeader>

          <ModuleContent>
            {agreementIds.map((id, index) => {
              const isExistingAgreement = /^0x[a-fA-F\d]{40}$/.test(id);
              let idProp = { uniqueId: id };
              if (isExistingAgreement) {
                idProp = { address: id };
              }

              return (
                <SubModule key={id} disableAccordian>
                  <SubModuleHeader
                    variant="large"
                    extra={
                      !isExistingAgreement &&
                      index !== 0 && (
                        <TextButton onClick={() => handleRemoveAgreement(id)} noPadding>
                          Remove
                        </TextButton>
                      )
                    }
                  >{`Royalty Fee #${index + 1}`}</SubModuleHeader>
                  <SubModuleContent>
                    <AgreementForm
                      contractAddress={contractAddress}
                      contractId={
                        !isExistingAgreement && index === 0 && contract ? contract.contractId : null
                      }
                      {...idProp}
                    />
                  </SubModuleContent>
                </SubModule>
              );
            })}

            <ModuleContentActions label="Add a Royalty Fee" padded className={styles.addAgreement}>
              <IconButton
                onClick={handleAddAgreement}
                tabIndex="0"
                iconProps={{ iconName: 'Add' }}
                ariaLabel="Add a Royalty Fee"
                className={styles.addRoyaltyFeeButton}
              />
            </ModuleContentActions>
          </ModuleContent>
        </Module>

        <div className={styles.buttonContainer}>
          <div className={styles.cancelButtonContainer}>
            <TextButton
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={areSubmitting || isSubmitting}
              noPadding
            >
              Cancel
            </TextButton>
          </div>

          <div className={styles.saveSubmitButtonContainer}>
            {!areSubmitting && !isSubmitting ? (
              <>
                <DefaultButton
                  className={styles.saveButton}
                  onClick={handleSave}
                  disabled={areSubmitting || isSubmitting}
                >
                  Save
                </DefaultButton>
                {contract.approvalStatus === 'Draft' && (
                  <PrimaryButton
                    onClick={handleSubmitForApproval}
                    disabled={areSubmitting || isSubmitting}
                  >
                    Submit for Approval
                  </PrimaryButton>
                )}
              </>
            ) : (
              <SavingIndicator className={styles.saving} />
            )}
          </div>
        </div>
      </Main>
    </>
  );
};

export default AddContractAgreementsV1;
